import React, { useState, useEffect } from "react";
import { MapPin, DollarSign } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { canPublishPropertyRequests } from "../constants/plans";
import {
  getPropertyRequestStatusBadgeClass,
  getPropertyRequestStatusLabel,
} from "../constants/propertyRequests";
import { MICROCOPY } from "../constants/microcopy";
import { api } from "../services/apiClient";
import BackNavigationButton from "../components/navigation/BackNavigationButton";

const PropertyRequests = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canPublish = canPublishPropertyRequests(user);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseForm, setResponseForm] = useState({
    responder_name: "",
    responder_email: "",
    responder_phone: "",
    property_url: "",
    message: "",
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get("/property-requests", { auth: false });
      const data = await response.json();
      setRequests(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (e) => {
    e.preventDefault();

    try {
      await api.post(
        `/property-requests/${respondingTo.id}/respond`,
        { ...responseForm, request_id: respondingTo.id },
        { multipart: false },
      );

      toast.success("Respuesta enviada al solicitante");
      setRespondingTo(null);
      setResponseForm({
        responder_name: "",
        responder_email: "",
        responder_phone: "",
        property_url: "",
        message: "",
      });
      fetchRequests();
    } catch (error) {
      toast.error("Error al enviar");
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-4">
          <BackNavigationButton dataTestId="property-requests-back" />
        </div>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Solicitudes de Búsqueda</h1>
            <p className="text-muted-foreground">
              Publica necesidades que aún no aparecen en el marketplace o responde búsquedas activas con inventario disponible.
            </p>
          </div>
          {canPublish && (
            <Button onClick={() => navigate("/create-request")}>
              Publicar Búsqueda
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-background border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1">
              Para quien busca
            </p>
            <p className="font-semibold">Publica una necesidad</p>
            <p className="text-sm text-muted-foreground mt-1">
              Si no encuentras una propiedad en el inventario actual, publica tu requerimiento con zona, presupuesto y prioridades.
            </p>
          </div>
          <div className="bg-background border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1">
              Para quien ofrece
            </p>
            <p className="font-semibold">Responde con propuesta</p>
            <p className="text-sm text-muted-foreground mt-1">
              Revisa búsquedas activas y comparte propiedades de tu inventario que sí cumplen con el contexto solicitado.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-background border border-border rounded-lg text-center py-12 px-6">
            <h2 className="text-xl font-semibold mb-2">
              {MICROCOPY.emptyStates.propertyRequests.title}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              {MICROCOPY.emptyStates.propertyRequests.description}
            </p>
            {user ? (
              canPublish ? (
                <div className="space-y-4">
                  <Button onClick={() => navigate("/create-request")}>
                    Publicar Búsqueda
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    También puedes volver luego para responder necesidades con tu inventario disponible.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-orange-600 font-medium">
                    ⭐ {MICROCOPY.emptyStates.propertyRequests.premiumGate}
                  </p>
                  <Button onClick={() => navigate("/pricing")}>
                    Ver Planes
                  </Button>
                </div>
              )
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {MICROCOPY.emptyStates.propertyRequests.loginGate}
                </p>
                <Button onClick={() => navigate("/login")}>
                  Iniciar Sesión
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map((request) => (
              <div
                key={request.id}
                className="bg-background border rounded-lg p-6"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-lg">{request.property_type}</h3>
                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${getPropertyRequestStatusBadgeClass(request.status)}`}
                  >
                    {getPropertyRequestStatusLabel(request.status)}
                  </span>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{request.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span>
                      ${request.budget_min.toLocaleString()} - $
                      {request.budget_max.toLocaleString()} MXN
                    </span>
                  </div>
                </div>

                {request.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2 italic">
                    "{request.description}"
                  </p>
                )}

                {request.required_features?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold mb-2">Requisitos:</p>
                    <div className="flex flex-wrap gap-1">
                      {request.required_features.map((f, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded-full"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {request.desired_features?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold mb-2">Deseadas:</p>
                    <div className="flex flex-wrap gap-1">
                      {request.desired_features.slice(0, 3).map((f, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  className="w-full mt-4"
                  size="sm"
                  onClick={() => setRespondingTo(request)}
                >
                  Responder a esta Búsqueda
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Modal de Respuesta */}
        <Dialog
          open={!!respondingTo}
          onOpenChange={(open) => !open && setRespondingTo(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Responder a Búsqueda</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRespond} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Comparte una respuesta breve y accionable: disponibilidad,
                ubicación y siguiente paso para avanzar.
              </p>
              <Input
                placeholder="Tu nombre *"
                value={responseForm.responder_name}
                onChange={(e) =>
                  setResponseForm({
                    ...responseForm,
                    responder_name: e.target.value,
                  })
                }
                required
              />
              <Input
                type="email"
                placeholder="Tu email *"
                value={responseForm.responder_email}
                onChange={(e) =>
                  setResponseForm({
                    ...responseForm,
                    responder_email: e.target.value,
                  })
                }
                required
              />
              <Input
                type="tel"
                placeholder="Tu teléfono *"
                value={responseForm.responder_phone}
                onChange={(e) =>
                  setResponseForm({
                    ...responseForm,
                    responder_phone: e.target.value,
                  })
                }
                required
              />
              <Input
                placeholder="Link a propiedad (opcional)"
                value={responseForm.property_url}
                onChange={(e) =>
                  setResponseForm({
                    ...responseForm,
                    property_url: e.target.value,
                  })
                }
              />
              <Textarea
                placeholder="Mensaje *"
                value={responseForm.message}
                onChange={(e) =>
                  setResponseForm({ ...responseForm, message: e.target.value })
                }
                rows={4}
                required
              />
              <Button type="submit" className="w-full">
                Enviar Respuesta
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Footer />
    </div>
  );
};

export default PropertyRequests;
